'use client';

import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { GrowwInstrument } from '../hooks/useGrowwInstruments';

const INSTRUMENTS_API_ENDPOINT = '/api/connectors/groww/instruments';

// Cache for instruments to avoid repeated API calls
const instrumentsCache: Record<string, GrowwInstrument[]> = {
  CASH: [],
  FNO: []
};

/**
 * Fetches instrument data from Groww API with pagination support
 * @param segment Segment to fetch (CASH or FNO)
 * @param search Optional search term
 * @returns Promise with array of instruments
 */
export async function getInstruments(
  segment: string = 'CASH',
  search?: string
): Promise<GrowwInstrument[]> {
  // If we have search query, always fetch from API
  if (search && search.length >= 2) {
    return fetchAllInstrumentsWithPagination(segment, search);
  }

  // If we have cached data, return it
  if (instrumentsCache[segment] && instrumentsCache[segment].length > 0) {
    console.log(`Using cached ${segment} instruments: ${instrumentsCache[segment].length} items`);
    return instrumentsCache[segment];
  }

  // Otherwise fetch from API and cache
  console.log(`Fetching all ${segment} instruments...`);
  const instruments = await fetchAllInstrumentsWithPagination(segment);
  
  // Log instrument types and counts for debugging
  if (segment === 'CASH') {
    const eqCount = instruments.filter(i => i.series === 'EQ').length;
    const beCount = instruments.filter(i => i.series === 'BE').length;
    const otherSeries = [...new Set(instruments.filter(i => i.series !== 'EQ' && i.series !== 'BE').map(i => i.series))];
    
    console.log(`CASH instruments breakdown: Total=${instruments.length}, EQ=${eqCount}, BE=${beCount}, Other series=${otherSeries.join(', ')}`);
  }
  
  instrumentsCache[segment] = instruments;
  return instruments;
}

/**
 * Fetch all instruments with pagination
 */
async function fetchAllInstrumentsWithPagination(
  segment: string,
  search?: string,
  pageSize: number = 1000
): Promise<GrowwInstrument[]> {
  let page = 1;
  let hasMore = true;
  const allInstruments: GrowwInstrument[] = [];
  
  while (hasMore) {
    console.log(`Fetching ${segment} instruments page ${page}...`);
    const { instruments, hasMore: moreAvailable } = await fetchInstrumentsPage(segment, page, pageSize, search);
    
    if (instruments.length === 0) {
      break;
    }
    
    allInstruments.push(...instruments);
    
    if (!moreAvailable) {
      hasMore = false;
    } else {
      page++;
    }
  }
  
  console.log(`Fetched total ${allInstruments.length} ${segment} instruments`);
  return allInstruments;
}

/**
 * Fetch instruments data from API for a specific page
 */
async function fetchInstrumentsFromAPI(
  segment: string = 'CASH',
  search?: string
): Promise<GrowwInstrument[]> {
  try {
    // Build the URL
    let url = `/api/connectors/groww/instruments?segment=${encodeURIComponent(segment)}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    // Set a large limit to fetch as many as possible
    url += '&limit=1000';

    console.log(`Fetching instruments from: ${url}`);
    
    // Make the API call
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch instruments: ${response.statusText}`);
    }

    const data = await response.json();
    console.log(`Received ${data.instruments?.length || 0} instruments from API`);
    return data.instruments || [];
  } catch (error) {
    console.error('Error fetching instruments:', error);
    throw error;
  }
}

/**
 * Fetches instruments with pagination support
 */
export async function fetchInstrumentsPage(
  segment: string = 'CASH', 
  page: number = 1, 
  limit: number = 1000,
  search?: string
): Promise<{ instruments: GrowwInstrument[]; hasMore: boolean; totalCount: number }> {
  try {
    const queryParams = new URLSearchParams({
      segment,
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      queryParams.append('search', search);
    }
    
    const url = `/api/connectors/groww/instruments?${queryParams.toString()}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch instruments: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    console.log(`Page ${page}: Received ${data.instruments?.length || 0}/${data.pagination?.totalCount || 0} instruments`);
    
    return {
      instruments: data.instruments || [],
      hasMore: data.pagination?.hasMore || false,
      totalCount: data.pagination?.totalCount || 0
    };
  } catch (error) {
    console.error('Error fetching instruments page:', error);
    return { instruments: [], hasMore: false, totalCount: 0 };
  }
} 